import React from 'react';
import {
  createIntl,
  RawIntlProvider,
  createIntlCache,
} from 'react-intl';
import PropTypes from 'prop-types';
import getTld from 'tld.js';
import enTranslationMessages from '../translations/en.json';
import esTranslationMessages from '../translations/es.json';
import frTranslationMessages from '../translations/fr.json';
import dkTranslationMessages from '../translations/dk.json';
import deTranslationMessages from '../translations/de.json';
import itTranslationMessages from '../translations/it.json';
import nlTranslationMessages from '../translations/nl.json';
import noTranslationMessages from '../translations/no.json';
import fiTranslationMessages from '../translations/fi.json';
import seTranslationMessages from '../translations/se.json';
import ukTranslationMessages from '../translations/uk.json';
import usTranslationMessages from '../translations/us.json';
import auTranslationMessages from '../translations/au.json';
import hrTranslationMessages from '../translations/hr.json';
import grTranslationMessages from '../translations/gr.json';
import plTranslationMessages from '../translations/pl.json';
import trTranslationMessages from '../translations/tr.json';
import { getConfig } from '../config/portal';
import { get, cloneDeep, findKey } from 'lodash';
import { getRouteConstants } from '../config/routes';
import { getBoatsConstants } from '../constants/boats';
import {
  generateSearchPath as generateBoatsSearchPath,
  parseSearchAppParams as parseBoatsSearchAppParams,
} from './urlHelpers/boats';
import {
  generateSearchPath as generateEnginesSearchPath,
  parseSearchAppParams as parseEnginesSearchAppParams
} from './urlHelpers/engines';

export const DEFAULT_LANGUAGE_CODE = getConfig().language;
export const DEFAULT_LOCALE = getConfig().locale || 'en-US';

const formatTranslationMessages = (language, messages) => {
  const defaultFormattedMessages =
    language !== DEFAULT_LANGUAGE_CODE
      ? formatTranslationMessages(DEFAULT_LANGUAGE_CODE, enTranslationMessages)
      : {};

  const supportedLanguages = getConfig().languages;
  const flattenFormattedMessages = (formattedMessages, key) => {
    if (supportedLanguages[language] === undefined) {
      return {};
    }
    const formattedMessage =
      supportedLanguages[language].overrideTranslations[key] ||
      messages[key] ||
      /* istanbul ignore next */
      defaultFormattedMessages[key];

    return Object.assign(formattedMessages, { [key]: formattedMessage });
  };

  return Object.keys(messages).reduce(flattenFormattedMessages, {});
};

const extendTranslations = (baseTranslations, overrideTranslations) => {
  const newTranslations = cloneDeep(baseTranslations);
  for (const key of Object.keys(overrideTranslations)) {
    newTranslations[key] = overrideTranslations[key];
  }
  return newTranslations;
};

export const translationMessages = {
  en: formatTranslationMessages('en', enTranslationMessages),
  es: formatTranslationMessages('es', esTranslationMessages),
  fr: formatTranslationMessages('fr', frTranslationMessages),
  dk: formatTranslationMessages('dk', dkTranslationMessages),
  de: formatTranslationMessages('de', deTranslationMessages),
  it: formatTranslationMessages('it', itTranslationMessages),
  nl: formatTranslationMessages('nl', nlTranslationMessages),
  no: formatTranslationMessages('no', noTranslationMessages),
  fi: formatTranslationMessages('fi', fiTranslationMessages),
  se: formatTranslationMessages('se', seTranslationMessages),
  hr: formatTranslationMessages('hr', hrTranslationMessages),
  gr: formatTranslationMessages('gr', grTranslationMessages),
  pl: formatTranslationMessages('pl', plTranslationMessages),
  tr: formatTranslationMessages('tr', trTranslationMessages),
  uk: formatTranslationMessages('uk', extendTranslations(enTranslationMessages, ukTranslationMessages)),
  us: formatTranslationMessages('us', extendTranslations(enTranslationMessages, usTranslationMessages)),
  au: formatTranslationMessages('au', extendTranslations(enTranslationMessages, auTranslationMessages)),
};

export const hrefLangs = {
  'au': 'en-AU',
  'de': 'de',
  'dk': 'da',
  'gr': 'el-GR',
  'en': 'en',
  'es': 'es',
  'fi': 'fi',
  'fr': 'fr',
  'hr': 'hr',
  'it': 'it',
  'nl': 'nl',
  'no': 'no',
  'se': 'sv',
  'pl': 'pl',
  'tr': 'tr',
  'uk': 'en-GB',
  'us': 'en-US'
};

export const htmlLanguage = {
  au: 'en-AU',
  de: 'de-DE',
  dk: 'da-DK',
  es: 'es-ES',
  fi: 'fi-FI',
  fr: 'fr-FR',
  gr: 'el-GR',
  hr: 'hr-HR',
  it: 'it-IT',
  nl: 'nl-NL',
  no: 'nn-NO',
  pl: 'pl-PL',
  se: 'sv-SE',
  tr: 'tr-TR',
  uk: 'en-GB',
  us: 'en-US'
};

const localeTranslations = {
  'en-US': 'us',
  'es-ES': 'es',
  'fr-FR': 'fr',
  'da-DK': 'dk',
  'de-DE': 'de',
  'it-IT': 'it',
  'nl-NL': 'nl',
  'no-NO': 'no',
  'fi-FI': 'fi',
  'se-SE': 'se',
  'en-GB': 'en',
  'en-UK': 'uk',
  'hr-HR': 'hr',
  'el-GR': 'gr',
  'pl-PL': 'pl',
  'tr-TR': 'tr',
  'en-AU': 'au'
};

const appLanguageCodes = {
  'en-US': 'en',
  'es-ES': 'es',
  'fr-FR': 'fr',
  'da-DK': 'dk',
  'de-DE': 'de',
  'it-IT': 'it',
  'nl-NL': 'nl',
  'nb-NO': 'no',
  'fi-FI': 'fi',
  'sv-SE': 'se',
  'en-GB': 'uk',
  'en-UK': 'uk',
  'hr-HR': 'hr',
  'el-GR': 'gr',
  'pl-PL': 'pl',
  'tr-TR': 'tr',
  'en-AU': 'au'
};

// Mapping to the routes constants
const pageTypes = {
  'HOMEPAGE': 'HOME_URL_PATTERN',
  'DEALERSEARCH': 'PARTY_SEARCH_URL_ROOT',
  'BOATS': 'SEARCH_URL_ROOT',
  'ENGINES': 'ENGINES_SEARCH_URL_ROOT',
  'HEALTHCHECK': 'HEALTHCHECK_URL_PATTERN',
  'BRANDED_SEARCH': 'BRANDED_SEARCH_URL_ROOT',
  'ENGINES_BRANDED_SEARCH': 'ENGINES_BRANDED_SEARCH_URL_ROOT',
  'BOAT_DETAILS': 'BOAT_DETAILS_ROOT',
  'ENGINE_DETAILS': 'ENGINE_DETAILS_ROOT',
  'BLOG': 'BLOG_URL_ROOT',
};

const languages = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  da: 'Dansk',
  de: 'Deutsch',
  it: 'Italiano',
  nl: 'Nederlands',
  no: 'Norsk',
  fi: 'suomi',
  sv: 'Svenska',
  dk: 'Dansk',
  se: 'Svenska',
  hr: 'Hrvatska',
  gr: 'Ελλάδα',
  pl: 'Polska',
  tr: 'Türkiye',
  us: 'English',
  au: 'English',
  uk: 'English'
};

const countries = {
  US: 'United States',
  ES: 'España',
  FR: 'France',
  DK: 'Danmark',
  DE: 'Deutschland',
  IT: 'Italia',
  NL: 'Nederland',
  NO: 'Norge',
  FI: 'Suomi',
  SE: 'Sverige',
  UK: 'United Kingdom',
  HR: 'Hrvatski',
  GR: 'Ελληνικά',
  PL: 'Polskie',
  TR: 'Türk',
  AU: 'Australia'
};

let globalI18n;

const buildFullUrl = (host, pathname, tld, subdomain, subDomainSupport, domain) => {
  const currentTld = '.' + getTld(host);
  let url = host.replace(currentTld,'$TLD');
  if (domain) {
    url = removeAndReplaceDomain(url, domain);
  }
  if (subDomainSupport){
    url = removeAndReplaceSubdomain(url, subdomain);
  }
  url = url.replace('$TLD',tld);
  return url + pathname;
};

const removeAndReplaceDomain = (urlWithoutTld, domain) => {
  let originalUrlPieces = urlWithoutTld.split('://');
  let [protocol, url] = originalUrlPieces;
  let urlPieces = url.split('.');
  let tld = urlPieces.pop().split('$').pop();
  urlPieces.push(`${domain}$${tld}`);
  let finalUrl = urlPieces.join('.');
  return `${protocol}://${finalUrl}`;
};

const removeAndReplaceSubdomain = (urlWithoutTld, subdomain) => {
  let originalUrlPieces = urlWithoutTld.split('://');
  let [protocol, url] = originalUrlPieces;
  let urlPieces = url.split('.');
  if (urlPieces.length === 2){
    if (subdomain){
      urlPieces[0] = subdomain;
    }
    else {
      urlPieces = [urlPieces[1]];
    }
  }
  else if (subdomain){
    urlPieces[0] = `${subdomain}.${urlPieces[0]}`;
  }
  let finalUrl = urlPieces.join('.');
  return `${protocol}://${finalUrl}`;
};

const onTranslationError = (err) => {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.error(err);
  }
};

const buildTempIntl = (locale) => {
  const langsConfigs = getConfig().languages;
  let tempIntl = createIntl({
    locale: locale,
    messages: translationMessages[locale],
    onError: onTranslationError
  });
  let addPathname = locale !== DEFAULT_LANGUAGE_CODE ? `/${locale}` : '';
  addPathname = langsConfigs[locale].type === 'tld' ||
    langsConfigs[locale].type === 'subdomain' ||
    langsConfigs[locale].type === 'domain' ?
    '' : addPathname;
  // localhost support
  if (getTld(globalI18n.host) === '') {
    addPathname = locale !== DEFAULT_LANGUAGE_CODE ? `/${locale}` : '';
  }

  const routes = getRouteConstants(tempIntl, addPathname);
  const boats = getBoatsConstants(routes, addPathname);
  return {
    intl: tempIntl,
    routes: routes,
    boats: boats,
    addPathname: addPathname,
  };
};

const translateLocation = (pathname, lang, pageType, intl) => {
  let route = '/';

  const normalTranslate = (pageType, append = '') => {
    return intl.routes[pageType] + append;
  };

  const searchBoatsTranslate = (pathname, isBranded = false) => {
    let searchParams = parseBoatsSearchAppParams(decodeURIComponent(pathname), isBranded);
    return generateBoatsSearchPath({}, searchParams, undefined, lang);
  };

  const searchEnginesTranslate = (pathname, isBranded = false) => {
    let searchParams = parseEnginesSearchAppParams(decodeURIComponent(pathname), isBranded);
    return generateEnginesSearchPath({}, searchParams, undefined, lang);
  };

  switch (pageType) {
  case 'HOMEPAGE':
    route = normalTranslate(pageTypes.HOMEPAGE);
    break;
  case 'BOAT_DETAILS':
    route = normalTranslate(pageTypes.BOAT_DETAILS, pathname.split('/').slice(-2)[0] + '/');
    break;
  case 'ENGINE_DETAILS':
    route = normalTranslate(pageTypes.ENGINE_DETAILS, pathname.split('/').slice(-2)[0] + '/');
    break;
  case 'DEALERSEARCH':
    route = normalTranslate(pageTypes.DEALERSEARCH);
    break;
  case 'BOATS':
    route = decodeURIComponent(searchBoatsTranslate(pathname));
    break;
  case 'BRANDED_SEARCH':
    route = searchBoatsTranslate(pathname, true);
    break;
  case 'ENGINES':
    route = searchEnginesTranslate(pathname);
    break;
  case 'ENGINES_BRANDED_SEARCH':
    route = searchEnginesTranslate(pathname, true);
    break;
  case 'HEALTHCHECK':
    route = normalTranslate(pageTypes.HEALTHCHECK);
    break;
  // Code used to translate Blog, rigth now Blog only supported on portal config lang
  case 'BLOG':
    route = normalTranslate(pageTypes.BLOG, pathname.split('/').slice(2)[0]);
    if (!route.endsWith('/')) { route += '/'; }
    break;
  }

  return route;
};

export const geti18nLocations = (pathname, isForSEOHead = false) => {
  const langsConfigs = getConfig().languages;
  let localizedLocations = [];
  let pageType = 'HOMEPAGE';
  let subDomainSupport = get(getConfig(), 'supports.subDomains', false);
  if (!getConfig().supports.multiLanguage) {
    return;
  }
  for (let lang in langsConfigs) {
    let tempIntl = buildTempIntl(lang);
    if (tempIntl.boats.getPageTypeFromPath(pathname)) {
      pageType = tempIntl.boats.getPageTypeFromPath(pathname);
    }
  }

  if (pageType === 'BLOG') {
    if (isForSEOHead) {
      return [];
    }
  }

  for (let lang in langsConfigs) {
    let tempIntl = buildTempIntl(lang);
    const supportsBlog = get(langsConfigs, `${lang}.supportsBlogHome`, false);
    if (pageType === 'BLOG' && !supportsBlog) {
      pageType = 'HOMEPAGE';
    }
    const route = translateLocation(pathname, lang, pageType, tempIntl);
    const domain = get(langsConfigs[lang], 'domain', false);
    let fullRoute = buildFullUrl(
      globalI18n.host,
      route,
      langsConfigs[lang].tld,
      get(langsConfigs[lang],'subdomain',''),
      subDomainSupport,
      domain
    );
    if (!process.env.USE_HTTP) {
      fullRoute = fullRoute.replace('http://','https://');
    }
    localizedLocations.push({
      lang: lang,
      route: fullRoute,
      tld: langsConfigs[lang].tld,
    });
  }

  return localizedLocations;
};

const setLocale = (locale, pathLocale, host = '') => {
  let cache = createIntlCache();
  const PATHS = ['/', ''];
  let addPathnameLocale = PATHS.includes(pathLocale) || !getConfig().supports.multiLanguage ? '' : `/${pathLocale}`;
  let intl = createIntl(
    {
      locale: locale,
      messages: translationMessages[locale],
      onError: onTranslationError
    },
    cache
  );

  let localizedRoutes = getRouteConstants(intl, addPathnameLocale);
  let localizedBoats = getBoatsConstants(localizedRoutes, addPathnameLocale);

  let url;
  if (host) {
    url = new URL(host);
  }

  globalI18n = {
    intl: intl,
    routesConstants: localizedRoutes,
    boatsConstants: localizedBoats,
    addPathnameLocale,
    host: host ? url.protocol + '//' + url.host : getConfig().host,
  };

  return globalI18n;
};

const removeProtocol = (tmpHost) => {
  if (tmpHost.includes('//')) {
    tmpHost = tmpHost.split('//');
    tmpHost.shift();
    tmpHost = tmpHost.join('');
  }
  return tmpHost;
};

const getSubdomain = (host) => {
  let tmpHost = '' + host;
  tmpHost = host.replace(`.${getTld(tmpHost)}`,'');
  tmpHost = removeProtocol(tmpHost);
  tmpHost = tmpHost.split('.');
  if (tmpHost.length === 1 || tmpHost[0] === 'www'){
    return '';
  }
  return tmpHost[0];
};

export const getDomain = (host) => {
  let tmpHost = '' + host;

  const portalTld = '.' + getTld(tmpHost);

  tmpHost = tmpHost.split(portalTld)[0];
  tmpHost = removeProtocol(tmpHost);

  // remove ports for "local" qa with hosted port
  if (tmpHost.includes(':')) {
    tmpHost = tmpHost.split(':');
    tmpHost.pop();
    tmpHost = tmpHost.join('');
  }

  tmpHost = tmpHost.split('.');
  return tmpHost[tmpHost.length - 1].split('/')[0];
};

// Set default locale at load
globalI18n = setLocale(appLanguageCodes[DEFAULT_LOCALE], '/');

export const getCurrentI18n = (props) => {
  if (props) {
    init(props);
  }
  return globalI18n;
};

export const LanguageContext = React.createContext();

export const getPathnameLanguage = () => getCurrentI18n().addPathnameLocale;

export const getLanguage = (lang) => languages[lang];

export const getCountry = (country) => countries[country];

export const getSupportedLanguages = () => Object.keys(getConfig().languages);

export const getCurrentLocale = (getLanguageCode = false) => {
  const currentCountryCode = getCurrentI18n().intl.locale;
  const currentLocale = findKey(localeTranslations, (countryCode) => countryCode === currentCountryCode);
  const currentLocaleLangConfig = get(getConfig().languages, currentCountryCode, {});
  const currentLocaleCountry = get(currentLocaleLangConfig, 'country', '');
  return getLanguageCode && currentLocaleCountry ? currentLocale : currentCountryCode;
};

export const getCurrentCurrency = () => {
  const currentLocale = getCurrentLocale();
  const formatingOptions = get(getConfig(),`languages.${currentLocale}.currency.format`);
  const currencyCode = get(getConfig(),`languages.${currentLocale}.currency.abbr`);
  const currencyInfo = get(getConfig(), `supportedCurrencies.${currencyCode}`);
  return currencyInfo ? {'currency': currencyInfo , 'formatingOptions': formatingOptions } : undefined;
};

export const getCurrentUom = () => {
  const currentLocale = getCurrentLocale();
  const currentUomInfo = get(getConfig(),`languages.${currentLocale}.uom`);
  return currentUomInfo;
};

export const mappedValue = (localizedValue, facetMessages, facetMessagesFormatter) => {
  const intl = globalI18n.intl;
  const englishValue = Object.keys(facetMessages).find(key => {
    const translatedValue = intl.formatMessage(facetMessages[key]);
    const formattedValue = facetMessagesFormatter ? facetMessagesFormatter(translatedValue) : translatedValue;
    return formattedValue === localizedValue;
  });
  return englishValue || localizedValue;
};

export const getCurrentRoutes = (overrideLocale) => {
  if (overrideLocale) {
    let tempIntl = buildTempIntl(overrideLocale);
    return tempIntl.routes;
  }
  return getCurrentI18n().routesConstants;
};

export const getCurrentBoats = (overrideLocale) => {
  if (overrideLocale) {
    let tempIntl = buildTempIntl(overrideLocale);
    return tempIntl.boats;
  }
  return getCurrentI18n().boatsConstants;
};

export const formatMessage = (message, params = {}, overrideLocale) => {
  if (overrideLocale) {
    let tempIntl = buildTempIntl(overrideLocale);
    return tempIntl.intl.formatMessage(message, params);
  }
  return getCurrentI18n().intl.formatMessage(message, params);
};

export const init = (props) => {
  let language = appLanguageCodes[DEFAULT_LOCALE];
  let pathLanguage = '/';
  let host;

  const getLanguageFromUrl = (pathname, host) => {
    const langConfigs = getConfig().languages;
    const pathFirstParam = pathname.split('/')[1];
    const currentTld = getTld(host) !== '' ? '.' + getTld(host) : undefined;
    const currentSubdomain = getSubdomain(host);
    const currentDomain = getDomain(host);
    let langSet = false;

    for (const lang in langConfigs) {
      if (!langSet) {
        const supportedTld = langConfigs[lang].tld;
        const supportedSubdomain = (langConfigs[lang].subdomain === 'www') ? '' : langConfigs[lang].subdomain;
        const supportedDomain = langConfigs[lang].domain;
        if (currentTld === supportedTld) {

          // domain type
          if (langConfigs[lang].type === 'domain'){
            if (currentDomain === supportedDomain) {
              language = lang;
              pathLanguage = '';
              langSet = true;
            }
            continue;
          }

          // folder type
          if (lang === pathFirstParam && langConfigs[lang].type === 'folder') {
            pathLanguage = lang;
            language = lang;
            langSet = true;
          }

          // tld type
          if ((!currentSubdomain || getBoatsConstants().IGNORED_SUBDOMAINS.includes(currentSubdomain)) &&
          langConfigs[lang].type === 'tld' && !supportedSubdomain &&
          (pathFirstParam === '' || !getSupportedLanguages().includes(pathFirstParam))) {
            language = lang;
            pathLanguage = '';
            langSet = true;
          }

          // subdomain type
          if (currentSubdomain === supportedSubdomain && langConfigs[lang].type === 'subdomain') {
            language = lang;
            pathLanguage = '';
            langSet = true;
          }

          // tld type with subdomain configured.
          if ((pathFirstParam === '' || !getSupportedLanguages().includes(pathFirstParam)) &&
            langConfigs[lang].type === 'tld' && supportedSubdomain === currentSubdomain) {
            language = lang;
            pathLanguage = '';
            langSet = true;
          }
        }
        // localhost support
        if (!currentTld) {
          if (lang === pathFirstParam) {
            pathLanguage = lang;
            language = lang;
            langSet = true;
          }
        }
      }
    }

    if (!langSet) {
      pathLanguage = '';
      language = getConfig().language;
    }
  };

  const parseLocation = (props) => {
    let serverPathname = get(props, 'location.location.pathname');
    let clientPathname = get(props, 'location.pathname', '/');
    host = get(props, 'host', '');
    getLanguageFromUrl(serverPathname || clientPathname, host);
  };

  parseLocation(props);
  globalI18n = setLocale(language, pathLanguage, host);

  return globalI18n;
};

export const CustomIntlProvider = (props) => {
  let i18n = getCurrentI18n(props);
  return (
    <LanguageContext.Provider value={i18n}>
      <RawIntlProvider value={i18n.intl}>
        {props.children}
      </RawIntlProvider>
    </LanguageContext.Provider>
  );
};

export const getPortalName = (context) => {
  const locale = getCurrentLocale();
  return get(context, `languages.${locale}.portal`, context.fullName);
};

CustomIntlProvider.propTypes = {
  children: PropTypes.node,
  pathname: PropTypes.string,
};
